<template>
  <component
    :is="type"
    v-bind="{
      ...(type === 'a' ? { href } : {}),
      ...(isDisabled || isLoading ? { disabled: true } : {}),
      class: buttonClass,
      ...attrs,
    }"
    @click="handleClick"
  >
    <slot></slot>
    <span v-if="isLoading" class="loader"></span>
  </component>
</template>

<script setup>
import { computed, useAttrs } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
  },
  onClick: {
    type: Function,
  },
  customClass: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'button',
    validator: (value) => ['a', 'button'].includes(value),
  },
  href: {
    type: String,
    default: '#',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const attrs = useAttrs();
const buttonClass = computed(() => `button button_${props.variant} ${props.customClass}`);
const isDisabled = computed(() => props.disabled);
const isLoading = computed(() => props.loading);

const handleClick = (event) => {
  if (isDisabled.value) {
    event.preventDefault();
    return;
  }

  if (props.onClick) {
    props.onClick();
  }
};
</script>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
.button {
  padding: 10px 24px;
  font-size: 12px;
  color: $white;
  background-color: transparent;
  transition: all 0.2s ease-in-out;
  border-width: 2px;
  border-style: solid;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  border-radius: $border-radius;
  text-transform: uppercase;

  &:hover {
    opacity: 0.7;
  }

  &[disabled] {
    opacity: 0.25;
  }

  &_primary {
    background-color: $primary;
    border-color: $primary;
    font-weight: 800;
  }

  &_secondary {
    background-color: $white;
    color: $secondary;
    border-color: $white;

    .loader {
      border: 2px solid $white;
      border-top: 2px solid $secondary;
    }
  }

  &_primary-outlined {
    border-color: $primary;
    color: $primary;
    font-weight: 800;

    .loader {
      border: 2px solid $white;
      border-top: 2px solid $primary;
    }
  }

  &_secondary-outlined {
    border-color: $white;
    color: $white;
  }
}

.loader {
  display: inline-block;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid $white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
